import { useEffect, useMemo, useRef, useState } from "react"
import { Card, Button, Space, Select } from "antd"
import { Toast } from "@/components/Toast"
import {
  PlusOutlined,
  DeleteOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import TrainingPlanTable from "./components/TrainingPlanTable"
import TrainingPlanModal from "./components/TrainingPlanModal"
import { useCrud } from "@/hooks/useCrud"
import {
  TrainingPlan,
  TrainingPlanFormData,
  TrainingPlanQueryParams,
  TrainingPlanSaveData,
  TrainingPlanStatus,
} from "./types"
import trainingPlanApi from "./api"
import studentApi from "../../people/student/api"
import { Student } from "../../people/student/types"
import { ID } from "@/types/common"
import domainLv2Api from "../../domain/domain-lv2/api"
import { DomainLv2 } from "../../domain/domain-lv2/types"
import { useDomainLv1 } from "@/hooks/useDomainLv1"

const { Option } = Select

const TrainingPlanManagement = () => {
  // 选中的学生ID
  const [selectedStudentId, setSelectedStudentId] = useState<ID | undefined>(
    undefined,
  )
  // 选中的状态
  const [selectedStatus, setSelectedStatus] = useState<
    TrainingPlanStatus | undefined
  >(undefined)
  const [selectedDomainLv1Id, setSelectedDomainLv1Id] = useState<
    ID | undefined
  >(undefined)
  const [selectedDomainLv2Id, setSelectedDomainLv2Id] = useState<
    ID | undefined
  >(undefined)
  // 学生列表
  const [students, setStudents] = useState<Student[]>([])
  // 学生列表加载状态
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [domainLv2s, setDomainLv2s] = useState<DomainLv2[]>([])
  const [domainLv2sLoading, setDomainLv2sLoading] = useState(false)
  const { domainLv1s, isLoading: domainLv1sLoading } = useDomainLv1()

  // 加载学生列表
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setStudentsLoading(true)
        const res = await studentApi.getList()
        setStudents(res.data || [])
        // 默认选中第一个学生
        /* if (data.length > 0 && !selectedStudentId) {
          setSelectedStudentId(data[0].id)
        } */
      } catch (error) {
        Toast.error("加载学生列表失败")
        console.error("加载学生列表失败:", error)
      } finally {
        setStudentsLoading(false)
      }
    }
    loadStudents()
  }, [])

  useEffect(() => {
    const loadDomainLv2s = async () => {
      if (!selectedDomainLv1Id) {
        setDomainLv2s([])
        return
      }
      try {
        setDomainLv2sLoading(true)
        const res = await domainLv2Api.getList(selectedDomainLv1Id)
        setDomainLv2s(res.data || [])
      } catch (error) {
        Toast.error("加载二级领域列表失败")
        console.error("加载二级领域列表失败:", error)
      } finally {
        setDomainLv2sLoading(false)
      }
    }

    loadDomainLv2s()
    setSelectedDomainLv2Id(undefined)
  }, [selectedDomainLv1Id])

  // 使用 useMemo 根据选中的学生和状态动态创建 api 对象
  const api = useMemo(() => {
    const queryParams: TrainingPlanQueryParams | null = selectedStudentId
      ? {
          studentId: selectedStudentId,
          ...(selectedStatus && { status: selectedStatus }),
          ...(selectedDomainLv1Id && { domainLv1Id: selectedDomainLv1Id }),
          ...(selectedDomainLv2Id && { domainLv2Id: selectedDomainLv2Id }),
        }
      : null

    return {
      getList: queryParams
        ? () => trainingPlanApi.getList(queryParams)
        : () =>
            Promise.resolve({
              data: [],
              total: 0,
              page: 1,
              pageSize: 0,
              totalPages: 0,
            }),
      getById: trainingPlanApi.getById,
      create: (data: TrainingPlanSaveData) => trainingPlanApi.create(data),
      update: (
        id: ID,
        data: TrainingPlanSaveData | Partial<TrainingPlanSaveData>,
      ) => trainingPlanApi.update(id, data as TrainingPlanFormData),
      delete: trainingPlanApi.delete,
    }
  }, [
    selectedStudentId,
    selectedStatus,
    selectedDomainLv1Id,
    selectedDomainLv2Id,
  ])

  const {
    items: trainingPlans,
    loading,
    opLoading,
    isModalOpen,
    editingItem: editingTrainingPlan,
    selectedIds,
    setSelectedIds,
    handleAdd,
    handleEdit,
    handleSave,
    handleDelete,
    handleBatchDelete,
    handleCloseModal,
    loadItems,
  } = useCrud<TrainingPlan, TrainingPlanSaveData, ID>({
    api,
    entityName: "训练计划",
    autoLoad: false, // 禁用自动加载，等学生默认值设置后再加载
  })

  const loadItemsRef = useRef(loadItems)

  useEffect(() => {
    loadItemsRef.current = loadItems
  }, [loadItems])

  // 处理学生筛选变化
  const handleStudentFilter = (studentId: ID) => {
    setSelectedStudentId(studentId)
    setSelectedIds([]) // 清空选中项
  }

  // 处理状态筛选变化
  const handleStatusFilter = (status: TrainingPlanStatus | undefined) => {
    setSelectedStatus(status)
    setSelectedIds([]) // 清空选中项
  }

  const handleDomainLv1Filter = (domainLv1Id: ID | undefined) => {
    setSelectedDomainLv1Id(domainLv1Id)
    setSelectedIds([])
  }

  const handleDomainLv2Filter = (domainLv2Id: ID | undefined) => {
    setSelectedDomainLv2Id(domainLv2Id)
    setSelectedIds([])
  }

  useEffect(() => {
    if (!selectedStudentId || !selectedDomainLv1Id) {
      return
    }
    setSelectedIds([])
    loadItemsRef.current()
  }, [selectedStatus, selectedDomainLv2Id])

  const handleSearch = () => {
    setSelectedIds([])
    if (selectedStudentId) {
      loadItems()
    } else {
      Toast.warning("请先选择学生")
    }
  }

  // 计算统计数据
  const selectedCount = selectedIds.length
  const batchDisabled = selectedCount === 0
  // 按钮禁用：必须选中学生
  const addDisabled = !selectedStudentId

  // 状态选项
  const statusOptions = [
    { label: "未开始", value: TrainingPlanStatus.NOT_STARTED },
    { label: "进行中", value: TrainingPlanStatus.IN_PROGRESS },
    { label: "已完成", value: TrainingPlanStatus.COMPLETED },
    { label: "已暂停", value: TrainingPlanStatus.SUSPENDED },
  ]

  return (
    <div className="p-6">
      {/* 主要内容区域 */}
      <Card>
        {/* 操作栏 */}
        <div className="flex justify-between items-center mb-4">
          <Space>
            <div className="flex items-center gap-2">
              <FilterOutlined className="text-gray-500" />
              <span className="text-sm text-gray-600">学生：</span>
              <Select
                placeholder="请选择学生"
                style={{ width: 200 }}
                value={selectedStudentId}
                onChange={handleStudentFilter}
                loading={studentsLoading}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {students.map((student) => (
                  <Option key={student.id} value={student.id}>
                    {student.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">一级领域：</span>
              <Select
                placeholder="请选择一级领域"
                style={{ width: 180 }}
                value={selectedDomainLv1Id}
                onChange={handleDomainLv1Filter}
                loading={domainLv1sLoading}
                allowClear
              >
                {domainLv1s.map((domain) => (
                  <Option key={domain.id} value={domain.id}>
                    {domain.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">二级领域：</span>
              <Select
                placeholder="请选择二级领域"
                style={{ width: 180 }}
                value={selectedDomainLv2Id}
                onChange={handleDomainLv2Filter}
                loading={domainLv2sLoading}
                disabled={!selectedDomainLv1Id}
                allowClear
              >
                {domainLv2s.map((domain) => (
                  <Option key={domain.id} value={domain.id}>
                    {domain.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">状态：</span>
              <Select
                placeholder="请选择状态"
                style={{ width: 180 }}
                value={selectedStatus}
                onChange={handleStatusFilter}
                allowClear
              >
                {statusOptions.map((option) => (
                  <Option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <Button
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                disabled={!selectedStudentId}
                data-testid="btn-search-training-plan"
              >
                查询
              </Button>
            </div>
          </Space>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              loading={opLoading}
              disabled={addDisabled}
              data-testid="btn-add-training-plan"
            >
              新增训练计划
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={batchDisabled}
              loading={opLoading}
              data-testid="btn-batch-delete"
            >
              批量删除 {selectedCount > 0 && `(${selectedCount})`}
            </Button>
          </Space>
        </div>

        {/* 训练计划表格 */}
        <TrainingPlanTable
          trainingPlans={trainingPlans}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </Card>

      {/* 训练计划信息模态框 */}
      <TrainingPlanModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        trainingPlan={editingTrainingPlan}
        loading={opLoading}
        students={students}
        studentsLoading={studentsLoading}
        initialValues={{
          studentId: selectedStudentId,
          domainId: selectedDomainLv1Id,
          domainLv2Id: selectedDomainLv2Id,
        }}
      />
    </div>
  )
}

export default TrainingPlanManagement
